// src/pages/ManagerListPage.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { 
    FiSearch, FiDownload, FiEye, FiX, FiCheckSquare, FiActivity, 
    FiFolder, FiCalendar, FiUser, FiFilter, FiChevronLeft, 
    FiChevronRight, FiTrendingUp, FiClock, FiMapPin, FiDollarSign, 
    FiPlus, FiLoader,FiFileText 
} from "react-icons/fi";

// Components
import SidebarNav from "../components/MasterSideBar.jsx";
import TopbarNav from "../components/TopbarNav";
import ProjectProgressCard from "../components/ProjectProgressCard.jsx";
import AddActivityModal from "../components/AddActivityModal.jsx";
import KPIListModal from "../components/programManager/MilestoneListModal.jsx";
import ViewProjectModal from "../components/ViewProjectModal";

// API
import { fetchProjects as fetchProjectsApi } from "../api/projectApi";
import { getActivitiesByProjectId as fetchActivitiesApi } from "../api/activityApi";

// Helper functions for user session
const getCurrentUser = () => {
    try {
        const userString = localStorage.getItem('user');
        return userString ? JSON.parse(userString) : null;
    } catch (e) {
        console.error('Error getting current user:', e);
        return null;
    }
};

const getCurrentUserId = () => {
    const user = getCurrentUser();
    return user ? (user._id || user.id || user.userId) : null;
};

// --- Sub-component: Activity List Modal ---
const ActivityListModal = ({ projectTitle, activities, onClose, activitiesLoading }) => {
    const modalRef = useRef();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) onClose();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-[100] backdrop-blur-sm p-4">
            <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 border-b border-orange-700">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-white/20 rounded-lg"><FiActivity className="w-6 h-6 text-white" /></div>
                            <div>
                                <h2 className="text-xl font-bold text-white uppercase tracking-tight">Activities</h2>
                                <p className="text-orange-100 text-xs">Project Breakdown</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg text-orange-200 hover:bg-white/20 hover:text-white transition-colors">
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">Project: {projectTitle}</h3>
                    
                    {activitiesLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <FiLoader className="animate-spin text-orange-500 text-3xl mb-3" />
                            <p className="text-gray-400 text-sm">Fetching records...</p>
                        </div>
                    ) : activities && activities.length > 0 ? (
                        <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            {activities.map((activity) => (
                                <div key={activity._id} className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-orange-200 transition-all">
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-800 text-sm">{activity.activityTitle}</h4>
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{activity.description}</p>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                            activity.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                            activity.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {activity.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <FiActivity className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm italic">No activities recorded for this project.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Main Page Component ---
const ManagerProjectListPage = () => {
    const navigate = useNavigate();
    const exportDropdownRef = useRef(null);

    // State
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const projectsPerPage = 10;

    // Modals state
    const [showAddActivityModal, setShowAddActivityModal] = useState(false);
    const [showActivityListModal, setShowActivityListModal] = useState(false);
    const [showMilestoneListModal, setShowMilestoneListModal] = useState(false);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

    const [showViewProjectModal, setShowViewProjectModal] = useState(false); // ✨ ADD THIS
    const [selectedProjectIdForView, setSelectedProjectIdForView] = useState(null); // ✨ ADD THIS
    
    // Selection state
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedProjectTitle, setSelectedProjectTitle] = useState("");
    const [projectActivities, setProjectActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [selectedProjectForMilestones, setSelectedProjectForMilestones] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const data = await fetchProjectsApi();
            const user = getCurrentUser();
            const userId = getCurrentUserId();
            setCurrentUser(user);
            
            if (!userId) {
                toast.error("Session expired. Please log in.");
                navigate("/");
                return;
            }
            
            const filtered = data.filter(p => {
                const managerId = typeof p.assignedProgramManager === 'object' 
                    ? (p.assignedProgramManager?._id || p.assignedProgramManager?.id) 
                    : p.assignedProgramManager;
                return String(managerId) === String(userId);
            });
            
            setProjects(filtered);
        } catch (error) {
            toast.error("Failed to sync projects.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewProjectProfile = (projectId) => {
        setSelectedProjectIdForView(projectId);
        setShowViewProjectModal(true);
    };    

    // restored handlers
    const handleViewActivities = async (projectId, projectTitle) => {
        try {
            setSelectedProjectId(projectId);
            setSelectedProjectTitle(projectTitle);
            setActivitiesLoading(true);
            const data = await fetchActivitiesApi(projectId);
            setProjectActivities(data);
            setShowActivityListModal(true);
        } catch (error) {
            toast.error("Failed to load activities.");
        } finally {
            setActivitiesLoading(false);
        }
    };

    const handleViewMilestones = (project) => {
        setSelectedProjectForMilestones(project);
        setShowMilestoneListModal(true);
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const filteredProjects = useMemo(() => {
        return projects.filter((p) => p.projectTitle.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [projects, searchTerm]);

    const indexOfLast = currentPage * projectsPerPage;
    const indexOfFirst = indexOfLast - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        if (filteredProjects.length === 0) return toast.info("No data to export");

        const exportData = filteredProjects.map(p => ({
            "Title": p.projectTitle,
            "Location": p.location,
            "Donor": Array.isArray(p.donor) ? p.donor.map(d => d.name).join(", ") : p.donor?.name || "N/A",
            "Progress": `${p.progress || 0}%`,
            "Status": p.status
        }));

        if (format === 'pdf') {
            const doc = new jsPDF("l", "pt", "a4");
            autoTable(doc, { head: [Object.keys(exportData[0])], body: exportData.map(r => Object.values(r)), headStyles: { fillColor: [237, 125, 49] } });
            doc.save("Project_Report.pdf");
        } else {
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Projects");
            XLSX.writeFile(wb, `Projects_Export.${format === 'excel' ? 'xlsx' : 'csv'}`);
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans">
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); navigate("/"); }}
            />

            <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchTerm} />

                <main className="p-4 md:p-8 mt-16 lg:mt-20 space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Project Portfolio</h1>
                            <p className="text-gray-500 text-sm">Real-time management of assigned program activities.</p>
                        </div>
                        {currentUser && (
                            <div className="flex items-center gap-3 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
                                <div className="p-2 bg-orange-100 rounded-xl text-orange-600"><FiUser size={20} /></div>
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase leading-none">Manager</p>
                                    <p className="text-sm font-bold text-gray-700">{currentUser.firstName} {currentUser.lastName}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: "Total Projects", val: projects.length, icon: <FiFolder />, color: "blue" },
                            { label: "Completed", val: projects.filter(p => p.status === 'Completed').length, icon: <FiCheckSquare />, color: "green" },
                            { label: "In Progress", val: projects.filter(p => p.status === 'In Progress').length, icon: <FiClock />, color: "purple" },
                            { label: "Avg Progress", val: `${projects.length > 0 ? Math.round(projects.reduce((s, p) => s + (p.progress || 0), 0) / projects.length) : 0}%`, icon: <FiTrendingUp />, color: "orange" }
                        ].map((stat, i) => (
                            <div key={i} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                                <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-600`}>{stat.icon}</div>
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase">{stat.label}</p>
                                    <p className="text-xl font-black text-gray-800">{stat.val}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="relative flex-1 max-w-md">
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input 
                                type="text" 
                                placeholder="Filter projects..." 
                                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border-none shadow-sm focus:ring-2 focus:ring-orange-500/20"
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="relative" ref={exportDropdownRef}>
                            <button 
                                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                                className="flex items-center gap-2 bg-white px-6 py-3 rounded-xl font-bold text-gray-700 shadow-sm hover:bg-gray-50 border border-gray-100"
                            >
                                <FiDownload /> Export
                            </button>
                            {exportDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                                    {['excel', 'csv', 'pdf'].map(fmt => (
                                        <button key={fmt} onClick={() => handleExport(fmt)} className="w-full px-4 py-3 text-left text-sm font-bold text-gray-600 hover:bg-orange-50 hover:text-orange-600 uppercase tracking-wider">
                                            {fmt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        {loading ? (
                            <div className="py-20 text-center"><FiLoader className="animate-spin mx-auto text-orange-500 mb-2" size={32} /></div>
                        ) : filteredProjects.length === 0 ? (
                            <div className="py-20 text-center text-gray-400 italic font-medium">No projects found.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50/50 border-b border-gray-100">
                                        <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                            <th className="px-6 py-5">Project Details</th>
                                            <th className="px-6 py-5">Location</th>
                                            <th className="px-6 py-5">Donor</th>
                                            <th className="px-6 py-5">Status / Progress</th>
                                            <th className="px-6 py-5 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {currentProjects.map((project) => (
                                            <tr key={project._id} className="hover:bg-gray-50/50 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <p className="font-bold text-gray-800 text-sm group-hover:text-orange-600 transition-colors">{project.projectTitle}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <FiCalendar className="text-gray-300" size={12} />
                                                        <p className="text-[10px] text-gray-400 font-bold">{new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-tight">
                                                    <div className="flex items-center gap-2"><FiMapPin className="text-orange-300" /> {project.location}</div>
                                                </td>
                                                <td className="px-6 py-4 text-xs font-bold text-gray-600 italic">
                                                    {Array.isArray(project.donor) ? project.donor.map(d => d.name).join(", ") : project.donor?.name || "N/A"}
                                                </td>
                                                <td className="px-6 py-4 min-w-[180px]">
                                                    <div className="flex flex-col gap-2">
                                                        <span className={`w-fit px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                                                            project.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                                        }`}>
                                                            {project.status}
                                                        </span>
                                                        <ProjectProgressCard project={project} />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center justify-center gap-2">

                                                        <button 
                                                            onClick={() => handleViewProjectProfile(project._id)}
                                                            className="p-2 bg-gray-100 text-gray-500 rounded-xl hover:bg-blue-100 hover:text-blue-600 transition-all"
                                                            title="View Project Profile"
                                                        >
                                                            <FiFileText size={18} />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleViewActivities(project._id, project.projectTitle)}
                                                            className="p-2 bg-gray-100 text-gray-500 rounded-xl hover:bg-orange-100 hover:text-orange-600 transition-all"
                                                        >
                                                            {activitiesLoading && selectedProjectId === project._id ? <FiLoader className="animate-spin" /> : <FiEye size={18} />}
                                                        </button>
                                                        <button 
                                                            onClick={() => handleViewMilestones(project)}
                                                            className="p-2 bg-gray-100 text-orange-600 rounded-xl hover:bg-orange-600 hover:text-white transition-all"
                                                        >
                                                            <FiCheckSquare size={18} />
                                                        </button>
                                                        <button 
                                                            onClick={() => { setSelectedProjectId(project._id); setShowAddActivityModal(true); }}
                                                            className="flex items-center gap-1 bg-orange-600 text-white px-3 py-2 rounded-xl text-[10px] font-bold uppercase hover:bg-orange-700 shadow-sm"
                                                        >
                                                            <FiPlus /> Activity
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                Page {currentPage} of {totalPages || 1}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="p-2 bg-white border border-gray-100 rounded-lg disabled:opacity-30"><FiChevronLeft /></button>
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="p-2 bg-white border border-gray-100 rounded-lg disabled:opacity-30"><FiChevronRight /></button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {showAddActivityModal && selectedProjectId && (
                <AddActivityModal 
                    projectId={selectedProjectId} 
                    onClose={() => setShowAddActivityModal(false)} 
                    onActivitySaved={() => { setShowAddActivityModal(false); fetchProjects(); }}
                />
            )}
            {showActivityListModal && (
                <ActivityListModal 
                    projectTitle={selectedProjectTitle} 
                    activities={projectActivities} 
                    onClose={() => setShowActivityListModal(false)} 
                    activitiesLoading={activitiesLoading} 
                />
            )}
            {showMilestoneListModal && selectedProjectForMilestones && (
                <KPIListModal 
                    isOpen={showMilestoneListModal} 
                    onClose={() => { setShowMilestoneListModal(false); fetchProjects(); }} 
                    project={selectedProjectForMilestones} 
                />
            )}

            {showViewProjectModal && selectedProjectIdForView && (
                <ViewProjectModal 
                    isOpen={showViewProjectModal} 
                    onClose={() => setShowViewProjectModal(false)} 
                    projectId={selectedProjectIdForView} 
                />
            )}
        </div>
    );
};

export default ManagerProjectListPage;